import React from 'react';
import { motion } from 'framer-motion';
import { LiveBadge } from './LiveBadge';
import { RefreshButton } from './RefreshButton';
import type { AnalyticsSummary } from '../types';

interface AnalyticsHeaderProps {
    summary?: AnalyticsSummary;
    periodLabel: string;
    isLoading: boolean;
    isStale: boolean;
    onRefresh: () => void;
    lastUpdate: string | null;
}

export const AnalyticsHeader: React.FC<AnalyticsHeaderProps> = ({
    summary,
    periodLabel,
    isLoading,
    isStale,
    onRefresh,
    lastUpdate,
}) => {
    return (
        <header className="px-[18px] pt-4 pb-2.5">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <motion.div 
                        className="flex items-center gap-2 mb-1"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <h1 className="text-[17px] font-semibold tracking-[-0.3px] text-[var(--text)]">
                            Аналитика
                        </h1>
                        <LiveBadge isConnected={!isStale} />
                    </motion.div>
                    
                    <p className="text-[11px] text-[var(--text-muted)]">
                        {summary ? (
                            <>
                                {summary.sitesMonitored} сайтов · период: {periodLabel}
                                {lastUpdate && (
                                    <span className="ml-2 text-[var(--text-secondary)]">
                                        · обновлено {lastUpdate}
                                    </span>
                                )}
                            </>
                        ) : (
                            'Загрузка данных...'
                        )}
                    </p>
                </div>

                <RefreshButton 
                    onClick={onRefresh} 
                    isRefreshing={isLoading}
                    lastUpdate={lastUpdate}
                />
            </div>

            {summary && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={`mt-3 p-2.5 rounded-lg text-[12px] flex items-center gap-2 ${
                        summary.slaMet 
                            ? 'bg-[var(--status-up-bg)] text-[var(--status-up-text)]' 
                            : 'bg-[var(--status-down-bg)] text-[var(--status-down-text)]'
                    }`}
                >
                    <span className="w-2 h-2 rounded-full bg-current animate-pulse" />
                    <span className="font-medium">
                        SLA {summary.slaTarget}%: {summary.slaMet ? 'выполняется' : 'нарушено'}
                    </span>
                    <span className="opacity-75 ml-auto">
                        Факт: {summary.overallUptime.toFixed(2)}%
                    </span>
                </motion.div>
            )}
        </header>
    );
};